import React, { useEffect, useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useApolloClient } from '@apollo/client';

import * as Styled from './GiftMessageForm.styled';
import Button from '../Button/Button';
import { useBreakpoint } from '../../util/Breakpoint.context';
import { setGiftMessage } from '../../services/storefront/cart';

const GiftMessageSchema = Yup.object().shape({
  note: Yup.string().required('A message is required.')
});

const GiftMessageBlocklist = new RegExp(/[^a-zA-Z0-9.? \n!_,:;\-)(%*$+=]+/g);

/**
 * Gift Message Form allows a user to attach a gift message to their cart for checkout
 */
const GiftMessageForm = ({ onComplete, onReject, note }) => {
  const client = useApolloClient();
  const currentBreakpoint = useBreakpoint();
  const [isFetching, setIsFetching] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    setTimeout(() => {
      inputRef.current?.focus();
    }, 200);
  }, []);

  // intercept value and santize before formik onchange event fires
  const handleSanitizeValue = useCallback((event) => {
    event.target.value = event.target.value.replace(GiftMessageBlocklist, ''); // eslint-disable-line
  });

  const handleSubmit = async ({ note: message }, { setErrors }) => {
    setIsFetching(true);

    await setGiftMessage(message, client)
      .then(onComplete)
      .catch((error) => {
        setErrors({
          note:
            error?.response?.message ||
            'Unable to contact server. Please try again.'
        });
      })
      .finally(() => setIsFetching(false));
  };

  return (
    <Styled.Container data-track-region="gift message input">
      <Formik
        initialValues={{ note }}
        validationSchema={GiftMessageSchema}
        validateOnBlur={false}
        onSubmit={handleSubmit}
      >
        <Styled.Form>
          <Styled.TextArea
            id="giftMessageFormNote"
            type="text"
            name="note"
            label="Message (No Special Characters)"
            rows={currentBreakpoint?.medium ? 10 : 3}
            limit={290}
            innerRef={inputRef}
            onChange={handleSanitizeValue}
          />
          <Styled.ButtonContainer>
            <Button
              data-track-label={note ? 'update message' : 'add message'}
              type="submit"
              size="large"
              variant="primary"
              isLoading={isFetching}
            >
              {note ? 'Update Message' : 'Add Message'}
            </Button>
            <Button
              data-track-label="cancel message"
              type="button"
              size="large"
              variant="defaultLight"
              onClick={onReject}
            >
              Cancel
            </Button>
          </Styled.ButtonContainer>
        </Styled.Form>
      </Formik>
    </Styled.Container>
  );
};

GiftMessageForm.propTypes = {
  /**
   * A function that is called upon successful gift message submission.
   */
  onComplete: PropTypes.func.isRequired,
  /**
   * A function that is called to close the form.
   */
  onReject: PropTypes.func.isRequired,
  /**
   * Prefilled value for the text area
   */
  note: PropTypes.string
};

GiftMessageForm.defaultProps = {
  note: ''
};

export default GiftMessageForm;
